import {createContext, useEffect, useState, useMemo, useCallback} from "react";
import Sidebar, {SidebarTab} from "../../components/Sidebar";
import TechnologiesMap from "../../components/TechnologiesMap";
import {
  initialStateInitiativeDataIds,
  initialStateInitiativeFilters,
} from "./StateInitiative.const";
import {DashboardContainer, TopBarContainer} from "./StateInitiative.styles";
import {
  IInitiative,
  IPeople,
  IPrevPeople,
  ISegment,
  IStateInitiativeDataIds,
} from "./StateInitiative.type";
import RadarChart from "../../components/RadarChart/RadarChart";
import useFetchDataStateInitiative from "../../hooks/useFetchDataStateInitiative";
import {useFilteredInitiatives} from "../../hooks/useFilteredInitiatives";
import {apiKeyData, apiKeyDataCRGCI, urlApiDataCRGCI} from "../../helper/api";
import SidebarInfo from "../../components/SidebarInfo";
import {IDataForSearchSidebar, ITypeRadar} from "../../types";
import {INFLUENCE_ZONE_MOCK_DATA} from "../../components/ClusterFilter/data";
import {SidebarCardContainer} from "../../ui/SidebarCardContainer";
import {AboutRadarTop} from "../../components/AboutRadarTop/AboutRadarTop";
import SidebarCardOrganisationsRadar from "../../components/SidebarCard/SidebarCardOrganisationsRadar";
import {
  InitiativeView,
  WorkerView,
} from "../../components/StateInitiativeViews";
import {useAuthStatus} from "../../hooks/useAuthStatus";
import {ImpactAreasProvider} from "../../components/ClusterFilter/ImpactAreasProvider";
import useInitiativePositions from "../../hooks/useInitiativePositions";

export const DataStateInitiativeIdsContext = createContext<any>(null);

const StateInitiativeDashboard = () => {
  const [activeSidebarTab, setActiveSidebarTab] = useState<SidebarTab>(
    SidebarTab.Empty
  );

  interface IRadarData {
    directions: ISegment[] | undefined;
    initiatives: IInitiative[] | undefined;
  }

  const [isLogin, setIsLogin] = useState(false);
  const [role, setRole] = useState<string | null>(null);

  const [isAuth, setIsAuth, roleAuth, setRoleAuth, userIdAuth, setUserIdAuth] =
    useAuthStatus([]);

  useEffect(() => {
    setIsLogin(isAuth);
    setRole(roleAuth);
  }, [isAuth, roleAuth]);

  const [isEditMode, setIsEditMode] = useState(false);
  const {positions: manualPositions, savePosition: setManualPosition, canEdit} = useInitiativePositions();

  const [isVisibleFiltersPanel, setIsVisibleFiltersPanel] = useState(false);
  const [isOpenSearchPanel, setIsOpenSearchPanel] = useState(false);
  const [isBenefitsSubsidies, setIsBenefitsSubsidies] = useState(false);

  const apiUrlCRGCI = `${urlApiDataCRGCI}/api/crgci`;

  if (!apiKeyData || !apiKeyDataCRGCI) {
    console.error("API keys missing:", {
      apiKeyData: !!apiKeyData,
      apiKeyDataCRGCI: !!apiKeyDataCRGCI,
    });
    throw new Error("API key не тот");
  }
  const {initiativesData, allData, driverData, participants, crgciData, error} =
    useFetchDataStateInitiative(apiUrlCRGCI, apiKeyDataCRGCI);

  const directions = useMemo(
    () => allData?.data?.segments,
    [allData?.data?.segments]
  );
  const authorities = allData?.data?.authorities;
  const initiatives = initiativesData?.data?.initiatives;

  useEffect(() => {
    if (driverData?.data?.initiatives) {
      const driversCount = driverData.data.initiatives.filter(
        (item) => item.driver
      ).length;
      console.log("Drivers count:", driversCount);
    }
  }, [initiatives, driverData]);

  useEffect(() => {
    if (error) {
      console.error("Fetch error:", error);
    }
  }, [error]);

  const searchDatasidebarInfo = {
    directions: directions,
    clusters: initiatives?.map((item) => {
      return {
        id: item.documentId,
        name: item.name,
        direction_id: item.segment?.documentId || "",
      };
    }),
  };

  const [isShowAllLabels, setIsShowAllLabels] = useState(true);
  const [isOpenPanel, setIsOpenPanel] = useState(false);
  const [isOpenSideBarInfo, setIsOpenSideBarInfo] = useState<boolean>(false);

  const [activeWorker, setActiveWorker] = useState({} as IPeople);
  const [arrPersonsWorker, setArrPersonsWorker] = useState([] as IPeople[]);
  const [idWorkerShowConnections, setIdWorkerShowConnections] = useState("");
  const [prevIdWorker, setPrevIdWorker] = useState<IPrevPeople[]>([]);
  const [isWorkerLoading, setIsWorkerLoading] = useState(false);

  const [isOpenSideBarStackHolders, setIsOpenSideBarStackHolders] =
    useState<boolean>(false);

  const [isOpenOrganisationsRadar, setIsOpenOrganisationsRadar] =
    useState<boolean>(false);
  const [selectedInitiativeTitle, setSelectedInitiativeTitle] =
    useState<string>("");

  const assignSegmentToInitiative = (initiative: any, segments: any[]) => {
    if (initiative.segment) return initiative;

    const name = initiative.name?.toLowerCase() || "";

    const segmentRules = [
      {
        segment: segments.find((s) => s.name === "Цифровая идентификация"),
        keywords: [
          "идентификация",
          "биометр",
          "госключ",
          "видеоидентификация",
          "профиль",
          "доверенност",
          "паспорт",
        ],
      },
      {
        segment: segments.find((s) => s.name === "Цифровые платежи"),
        keywords: [
          "платеж",
          "рубль",
          "антифрод",
          "эквайринг",
          "ипотека",
          "кредит",
          "облигац",
          "криптовалют",
          "платформа",
        ],
      },
      {
        segment: segments.find((s) => s.name === "Цифровой обмен данными"),
        keywords: ["api", "данн", "согласий", "волна", "экосистем"],
      },
    ];

    for (const rule of segmentRules) {
      if (
        rule.segment &&
        rule.keywords.some((keyword) => name.includes(keyword))
      ) {
        return {...initiative, segment: rule.segment};
      }
    }

    return {
      ...initiative,
      segment: segments.length > 0 ? segments[0] : null,
    };
  };

  const participantsData = useMemo(() => {
    return (
      initiativesData?.data?.initiatives?.map((item: any) => {
        const enhancedItem = {
          ...item,
          // Данные из /api/initiatives уже содержат все поля с populate=*
          maturity: item.maturity || "50",
          statusInBank: item.statusInBank || 2,
        };

        if (directions) {
          return assignSegmentToInitiative(enhancedItem, directions);
        }
        return enhancedItem;
      }) || []
    );
  }, [initiativesData, directions]);

  const [radarData, setRadarData] = useState<IRadarData>({} as IRadarData);

  useEffect(() => {
    setRadarData({
      directions: directions,
      initiatives: participantsData,
    });
  }, [directions, participantsData]);

  // radarData = {
  //   directions: directions,
  //   initiatives: initiatives,
  // };

  const [isOpenSideBar, setIsOpenSideBar] = useState<boolean>(false);

  const [isSearchPanelActive, setIsSearchPanelActive] = useState(false);
  const [filters, setFilters] = useState(initialStateInitiativeFilters);

  const filteredInitiatives = useFilteredInitiatives({
    participantsData: participantsData || [],
    filters,
    isBenefitsSubsidies,
  });

  useEffect(() => {
    setRadarData({
      directions: directions || [],
      initiatives: filteredInitiatives,
    });
  }, [filteredInitiatives, directions]);

  const [cpuStep, setCpuStep] = useState(1);
  const [activeTab, setActiveTab] = useState(SidebarTab.Empty);
  const [activeClusterId, setActiveClusterId] = useState<
    number | string | null | undefined
  >();
  const [activeDirectionId, setActiveDirectionId] = useState<
    number | string | null | undefined
  >();

  const [dataIds, setDataIds] = useState<IStateInitiativeDataIds>(
    initialStateInitiativeDataIds
  );

  useEffect(() => {
    if (idWorkerShowConnections) {
      setIsOpenSideBarStackHolders(true);
    }
  }, [idWorkerShowConnections]);

  useEffect(() => {
    if (activeSidebarTab === SidebarTab.Directions) {
      setActiveTab(SidebarTab.Directions);
    }
    if (activeSidebarTab === SidebarTab.Projects) {
      setActiveTab(SidebarTab.Directions);
      setIsOpenSideBar(true);
    }
    if (activeSidebarTab === SidebarTab.CPU) {
      cpuStep === 1
        ? setActiveTab(SidebarTab.Projects)
        : setActiveTab(SidebarTab.CPU);
      setIsOpenSideBar(true);
    }
    if (activeSidebarTab === SidebarTab.Trends) {
      setActiveTab(SidebarTab.Trends);
      setIsOpenSideBar(true);
    }
    if (activeSidebarTab === SidebarTab.WeakSignal) {
      setActiveTab(SidebarTab.WeakSignal);
      setIsOpenSideBar(true);
    }
    if (activeSidebarTab === SidebarTab.StateInitiative) {
      setActiveTab(SidebarTab.StateInitiative);
      setIsOpenSideBar(true);
    }
  }, [activeSidebarTab, cpuStep]);

  const openSidebarWithTab = (
    tab: SidebarTab,
    directionId: number | string,
    trendId?: number | string,
    trendTitle?: string,
    sectionName?: string,
    segmentColor?: {back: string; color: string},
    nameTehnology?: string,
    idTehnology?: number | string,
    typeRadar?: ITypeRadar,
    isSearchPanelActive?: boolean
  ) => {
    setCpuStep(1);
    setActiveClusterId(trendId);
    setActiveDirectionId(directionId);

    let initiativeData = null;
    if (tab === SidebarTab.StateInitiative && trendId && participantsData) {
      initiativeData = participantsData.find(
        (initiative) => initiative.documentId === trendId
      );
    }

    setDataIds({
      directionId,
      trendId: trendId !== undefined ? trendId : null,
      trendName: trendTitle || "",
      directionName: sectionName || "",
      colorTitle: segmentColor?.back || "#A774FF",
      initiativeData: initiativeData,
    });
    setActiveSidebarTab(tab);
    setActiveTab(SidebarTab.StateInitiative);
    setIsOpenPanel(false);
    if (isSearchPanelActive) {
      setIsSearchPanelActive(true);
    } else {
      setIsSearchPanelActive(false);
    }
    setIsOpenSideBarInfo(false);
  };

  const resetToDefaultTab = () => {
    if (isSearchPanelActive) setIsOpenPanel(true);
    setTimeout(() => {
      setActiveSidebarTab(SidebarTab.Empty);
      setCpuStep(1);
      setActiveClusterId(null);
      setActiveDirectionId(null);
    }, 600);
    setDataIds({
      directionId: null,
      trendId: null,
      trendName: "",
      directionName: "",
      colorTitle: "",
      initiativeData: null,
    });
    setIsOpenSideBar(false);
    setActiveTab(SidebarTab.Empty);
    setIsOpenSideBarStackHolders(false);
    setIsOpenSideBarInfo(false);
    setIsOpenOrganisationsRadar(false);
    setSelectedInitiativeTitle("");
  };

  const openOrganisationsRadar = (initiativeTitle: string) => {
    setSelectedInitiativeTitle(initiativeTitle);
    setIsOpenOrganisationsRadar(true);
  };

  const closeOrganisationsRadar = () => {
    setIsOpenOrganisationsRadar(false);
    setSelectedInitiativeTitle("");
  };
  const toggleFilter = useCallback<
    <T>(filter?: string, value?: T, isReset?: boolean) => void
  >((filter, value, isReset = false) => {
    if (isReset) {
      setFilters(initialStateInitiativeFilters);
      return;
    }
    if (!filter) return;
    if (!(filter in initialStateInitiativeFilters)) return;

    const filterKey = filter as keyof typeof initialStateInitiativeFilters;
    const defaultValue = initialStateInitiativeFilters[filterKey]?.value;

    // Специальная логика для influence_zone
    let turnOnValue = false;

    if (filterKey === "influence_zone") {
      // Получаем общее количество доступных зон влияния
      const totalZones = INFLUENCE_ZONE_MOCK_DATA.length;

      // Если выделены ВСЕ зоны, то фильтр НЕ активен (показываем всё)
      // Если выделены НЕ ВСЕ зоны, то фильтр активен
      if (Array.isArray(value)) {
        turnOnValue = value.length > 0 && value.length < totalZones;
      }
    } else {
      // Логика для других фильтров
      turnOnValue =
        value === undefined || value === defaultValue
          ? false
          : filterKey === "lifecycle_stage" ||
            filterKey === "obligation_level" ||
            filterKey === "participation_formats"
          ? Array.isArray(value) && value.length === 0
            ? false
            : true
          : Array.isArray(value) && value.length === 0
          ? false
          : true;
    }

    setFilters((prevFilters: any) => ({
      ...prevFilters,
      [filter]: {
        value: value !== undefined ? value : !prevFilters[filter],
        turnOn: turnOnValue,
      },
    }));
  }, []);
  return (
    <ImpactAreasProvider>
      <DataStateInitiativeIdsContext.Provider value={{dataIds, setDataIds}}>
        <DashboardContainer>
          <Sidebar
            setIsLogin={setIsLogin}
            setRole={setRole}
            isLogin={isLogin}
            role={role}
            activeTab={activeTab}
            typeRadar="stateInitiative"
            setIsOpenSideBarInfo={setIsOpenSideBarInfo}
            isOpenSideBarInfo={isOpenSideBarInfo}
            isOpenSideBar={isOpenSideBar}
            searchDatasidebarInfo={
              searchDatasidebarInfo as IDataForSearchSidebar
            }
            setIsOpenPanel={setIsOpenPanel}
            isOpenPanel={isOpenPanel}
            openSidebarWithTab={openSidebarWithTab}
            onFilterChange={toggleFilter}
            filters={filters}
            isVisibleFiltersPanel={isVisibleFiltersPanel}
            setIsVisibleFiltersPanel={setIsVisibleFiltersPanel}
            isShowAllLabels={isShowAllLabels}
            setIsShowAllLabels={setIsShowAllLabels}
            setIsOpenSearchPanel={setIsOpenSearchPanel}
            authorities={authorities}
            isActiveWorker={Object.keys(activeWorker || {}).length > 0}
            isOpenSideBarStackHolders={isOpenSideBarStackHolders}
            resetToDefaultTab={resetToDefaultTab}
            isBenefitsSubsidies={isBenefitsSubsidies}
            setIsBenefitsSubsidies={setIsBenefitsSubsidies}
            isEditMode={isEditMode}
            onToggleEditMode={() => setIsEditMode((v) => !v)}
            canEditPositions={canEdit}
          />

          <SidebarCardContainer isOpenSideBar={isOpenSideBar}>
            {isOpenSideBarStackHolders ? (
              <WorkerView
                activeWorker={activeWorker}
                setActiveWorker={setActiveWorker}
                arrPersonsWorker={arrPersonsWorker}
                setArrPersonsWorker={setArrPersonsWorker}
                idWorkerShowConnections={idWorkerShowConnections}
                setIdWorkerShowConnections={setIdWorkerShowConnections}
                prevIdWorker={prevIdWorker}
                setPrevIdWorker={setPrevIdWorker}
                isWorkerLoading={isWorkerLoading}
                setIsWorkerLoading={setIsWorkerLoading}
                isOpenSideBarStackHolders={isOpenSideBarStackHolders}
                setIsOpenSideBarStackHolders={setIsOpenSideBarStackHolders}
                openSidebarWithTab={openSidebarWithTab}
                isLogin={isLogin}
                role={role}
                userIdAuth={userIdAuth}
                roleAuth={roleAuth}
              />
            ) : (
              <InitiativeView
                isLogin={isLogin}
                role={role}
                activeSidebarTab={activeSidebarTab}
                resetToDefaultTab={resetToDefaultTab}
                setActiveSidebarTab={setActiveSidebarTab}
                cpuStep={cpuStep}
                setCpuStep={setCpuStep}
                isOpenSideBarStackHolders={isOpenSideBarStackHolders}
                setIsOpenSideBarStackHolders={setIsOpenSideBarStackHolders}
                activeWorker={activeWorker}
                setActiveWorker={setActiveWorker}
                idWorkerShowConnections={idWorkerShowConnections}
                setIdWorkerShowConnections={setIdWorkerShowConnections}
                prevIdWorker={prevIdWorker}
                setPrevIdWorker={setPrevIdWorker}
                openOrganisationsRadar={openOrganisationsRadar}
                crgciData={crgciData}
                arrPersonsWorker={arrPersonsWorker}
                initiativeData={dataIds.initiativeData || null}
              />
            )}
          </SidebarCardContainer>

          {/* <SidebarCardContainer isOpenSideBar={isOpenOrganisationsRadar}>
          {isOpenOrganisationsRadar && (
            <SidebarCardOrganisationsRadar
              onClose={closeOrganisationsRadar}
              arrPersonsWorker={arrPersonsWorker}
              setIdWorkerShowConnections={setIdWorkerShowConnections}
              prevIdWorker={prevIdWorker}
              setPrevIdWorker={setPrevIdWorker}
              initiativeTitle={selectedInitiativeTitle}
            />
          )}
        </SidebarCardContainer> */}

          <SidebarCardContainer isOpenSideBar={isOpenSideBarInfo}>
            <SidebarInfo
              typeRadar="stateInitiative"
              close={setIsOpenSideBarInfo}
            />
          </SidebarCardContainer>

          <TopBarContainer>
            <AboutRadarTop typeRadar={"stateInitiative"} />
            <TechnologiesMap typeRadar={"stateInitiative"} isLogin={isLogin} />
          </TopBarContainer>

          <RadarChart
            openSidebarWithTab={openSidebarWithTab}
            radarData={radarData}
            activeClusterId={activeClusterId}
            activeDirectionId={activeDirectionId}
            typeRadar="stateInitiative"
            isShowAllLabels={isShowAllLabels}
            isOpenSideBarInfo={isOpenSearchPanel}
            setIdWorkerShowConnections={setIdWorkerShowConnections}
            isEditMode={isEditMode}
            manualPositions={manualPositions}
            onPositionChange={setManualPosition}
          />
        </DashboardContainer>
      </DataStateInitiativeIdsContext.Provider>
    </ImpactAreasProvider>
  );
};

export default StateInitiativeDashboard;
